import React, { createContext, useContext, useEffect, useState } from 'react';
import { apiClient, type LoginRequest, type User } from '../lib/api';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (data: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = user !== null;

  // Initialize auth state on app load
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await apiClient.getCurrentUser();
        // Check if user is admin
        // response.data is the user object directly (not nested under user)
        if (!response.data.isAdmin) {
          console.warn('User is not admin, logging out');
          localStorage.removeItem('auth_token');
          localStorage.removeItem('auth_session_id');
          setIsLoading(false);
          return;
        }
        setUser(response.data);
      } catch (error) {
        // Token is invalid, remove it
        console.error('getCurrentUser error:', error);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_session_id');
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (data: LoginRequest) => {
    try {
      const response = await apiClient.login(data);

      // Check if user is admin
      if (!response.data.user.isAdmin) {
        throw new Error('Access denied. Admin privileges required.');
      }

      // Store token and session ID
      localStorage.setItem('auth_token', response.data.token);
      localStorage.setItem('auth_session_id', response.data.sessionId);

      // Set user in state
      setUser(response.data.user);
    } catch (error) {
      // Re-throw error so components can handle it
      throw error;
    }
  };

  const logout = async () => {
    try {
      // Call API logout endpoint
      await apiClient.logout();
    } catch (error) {
      // Even if API call fails, we still want to logout locally
      console.warn('Logout API call failed:', error);
    } finally {
      // Clear local storage and state
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_session_id');
      setUser(null);
    }
  };

  const refreshUser = async () => {
    try {
      const response = await apiClient.getCurrentUser();
      // Check if user is still admin
      // response.data is the user object directly (not nested under user)
      if (!response.data.isAdmin) {
        await logout();
        throw new Error('Admin privileges revoked.');
      }
      setUser(response.data);
    } catch (error) {
      // Token is invalid, logout user
      await logout();
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
